import { ScrollView } from "@-/com.npmjs.react-native";
import {
  useGlassHeaderInset,
  useHeaderScrollContentStyle,
} from "@-/dev.improve.haniverse.components.ui.header-scroll";
import {
  createSectionAccordionViewport,
  SectionAccordionViewportContext,
} from "@-/dev.improve.haniverse.components.ui.section-accordion";
import { DeckSectionScrollContext } from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.viewport";
import {
  DeckCardSurface,
  type DeckCardSurfaceProps,
} from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section.surface";
import { useScrollEdgeScrollerRegistration } from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

type RowBoundariesChangeHandler = (boundaries: readonly number[], rowPitch: number) => void;
type SurfaceRows = { boundaries: readonly number[]; rowPitch: number };

const DAY_SURFACE_KEY = "deck-upcoming-day-surface";
const QUEUED_SURFACE_KEY = "deck-upcoming-queued-surface";
const NO_ROWS: SurfaceRows = { boundaries: [], rowPitch: 0 };

export function DeckUpcomingCardsSurface({
  bottomInset,
  onRowBoundariesChange,
  queued,
  reviews,
  scrollOffset,
}: {
  bottomInset: number;
  onRowBoundariesChange: RowBoundariesChangeHandler;
  queued?: DeckCardSurfaceProps;
  reviews: DeckCardSurfaceProps;
  scrollOffset: SharedValue<number>;
}) {
  const [viewport] = useState(createSectionAccordionViewport);
  const scrollRef = useRef<ScrollView>(null);
  const viewportHeight = useRef(0);
  const rowsByBody = useRef(new Map<string, SurfaceRows>());
  const hasQueued = queued !== undefined;
  const reportBodyBoundaries = useCallback(
    (bodyKey: string, boundaries: readonly number[], rowPitch: number) => {
      rowsByBody.current.set(bodyKey, { boundaries, rowPitch });
      const reviewsRows = rowsByBody.current.get(DAY_SURFACE_KEY) ?? NO_ROWS;
      const queuedRows = hasQueued
        ? (rowsByBody.current.get(QUEUED_SURFACE_KEY) ?? NO_ROWS)
        : NO_ROWS;
      // Snaps past the content continue the rhythm of the lowest surface that has rows.
      const lastRows = queuedRows.boundaries.length > 0 ? queuedRows : reviewsRows;
      onRowBoundariesChange(
        [...reviewsRows.boundaries, ...queuedRows.boundaries].sort((a, b) => a - b),
        lastRows.rowPitch,
      );
    },
    [hasQueued, onRowBoundariesChange],
  );
  const reportReviewsBoundaries = useCallback(
    (boundaries: readonly number[], rowPitch: number) =>
      reportBodyBoundaries(DAY_SURFACE_KEY, boundaries, rowPitch),
    [reportBodyBoundaries],
  );
  const reportQueuedBoundaries = useCallback(
    (boundaries: readonly number[], rowPitch: number) =>
      reportBodyBoundaries(QUEUED_SURFACE_KEY, boundaries, rowPitch),
    [reportBodyBoundaries],
  );
  const topInset = useGlassHeaderInset() ?? 0;
  const contentContainerStyle = useHeaderScrollContentStyle({
    contentContainerStyle: { paddingBottom: bottomInset },
    headerOverlap: topInset,
    suppressContentGap: topInset > 0,
  });
  useScrollEdgeScrollerRegistration();
  useLayoutEffect(() => {
    scrollOffset.set(0);
  }, [scrollOffset]);
  useLayoutEffect(() => {
    viewport.retainBodies(
      new Set(hasQueued ? [DAY_SURFACE_KEY, QUEUED_SURFACE_KEY] : [DAY_SURFACE_KEY]),
    );
  }, [hasQueued, viewport]);
  useLayoutEffect(() => {
    viewport.setViewport(Math.max(0, viewportHeight.current - bottomInset));
  }, [bottomInset, viewport]);
  const correctOffset = useCallback(
    (y: number) => scrollRef.current?.scrollTo({ animated: false, y }),
    [],
  );
  return (
    <DeckSectionScrollContext value={correctOffset}>
      <SectionAccordionViewportContext value={viewport}>
        <ScrollView
          className="min-h-0 flex-1"
          contentContainerStyle={contentContainerStyle}
          contentInsetAdjustmentBehavior="never"
          onLayout={({ nativeEvent: { layout } }) => {
            viewportHeight.current = layout.height;
            viewport.setViewport(Math.max(0, layout.height - bottomInset));
          }}
          onScroll={({ nativeEvent }) => {
            scrollOffset.set(Math.max(0, nativeEvent.contentOffset.y));
            viewport.setViewport(
              Math.max(0, viewportHeight.current - bottomInset),
              nativeEvent.contentOffset.y,
            );
          }}
          ref={scrollRef}
          scrollEventThrottle={16}
          scrollIndicatorInsets={{ bottom: bottomInset, top: topInset }}
          testID="deck-upcoming-cards-scroll"
        >
          <DeckCardSurface
            {...reviews}
            bodyKey={DAY_SURFACE_KEY}
            onRowBoundariesChange={reportReviewsBoundaries}
          />
          {queued === undefined ? null : (
            <DeckCardSurface
              {...queued}
              bodyKey={QUEUED_SURFACE_KEY}
              onRowBoundariesChange={reportQueuedBoundaries}
            />
          )}
        </ScrollView>
      </SectionAccordionViewportContext>
    </DeckSectionScrollContext>
  );
}
